import { AfterViewInit, Component, ElementRef, Input, Output, EventEmitter } from '@angular/core';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import * as L from 'mapbox.js';

import { MapifyMap } from '../../models/MapifyMap';
import { Style } from '../../models/Style';
import { Coordinate } from '../../models/Coordinate';
import { Bounds } from '../../models/Bounds';
import { Layer, LayerPosition } from '../../models/Layer';

import { environment } from '../../../environments/environment';
import { MapifyZoom } from '../../models/MapifyZoom';
import { Finish } from '../../models/Finish';
import { Size } from '../../models/Size';
import { Line, LineBorder, LineDim } from 'app/models/Line';
import { MapifyMapText } from '../../models/MapifyMapText';
import { Icon } from '../../models/Icon';

/**
 * Given a currentZoomLevel, a list of zoomLevels and the zoomDirection it will return the "next" zoomLevel. In case
 * the zoomLevel has reached the boundaries (minumum and maximum zoomLevel) it will return the same zoomLevel.
 */
function getZoomLevel(currentZoomLevel: MapifyZoom, zoomLevels: MapifyZoom[], zoomDirection: 'in' | 'out'): MapifyZoom {
  const { zoom, scaling } = currentZoomLevel;

  return zoomLevels.reduce((acc, zoomLevel, index) => {
    if (zoomLevel.zoom === zoom && zoomLevel.scaling === scaling) {
      if (zoomDirection === 'in' && index < zoomLevels.length - 1) {
        acc = zoomLevels[index + 1];
      }

      if (zoomDirection === 'out' && index > 0) {
        acc = zoomLevels[index - 1];
      }
    }

    return acc;
  }, currentZoomLevel);
}
@Component({
  selector: 'mpf-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss']
})
export class MapComponent implements AfterViewInit {
  public lines: Line[] = [];
  public borders: Layer[] = [];
  public zoomLevels: MapifyZoom[] = environment.mapify.zoomLevels;
  private mapboxMap;
  private mapInitialized = false;
  private heightRefElement: HTMLElement;

  @Output() mapZoomChange = new EventEmitter<MapifyZoom>();
  @Output() mapCenterChange = new EventEmitter<Coordinate>();
  @Output() mapBoundsChange = new EventEmitter<Bounds>();
  @Output() mapIconsChange = new EventEmitter<Icon[]>();

  // private props required by @Input() setters
  private _mapSize: Size;
  private _mapZoom: MapifyZoom;
  private _mapCenter: Coordinate;
  private _mapBounds: Bounds;
  private _mapStyle: Style;
  private _mapFinish: Finish;
  private _mapIcons: Icon[] = [];

  private markers: any[] = [];

  @Input() map: MapifyMap;

  @Input()
  set mapSize(mapSize) {
    this._mapSize = mapSize;

    this.invalidateMapSize();

    this.setStyleSizeFinishDependingProperties();
  }

  get mapSize(): Size {
    return this._mapSize;
  }

  @Input()
  set mapZoom(zoom: MapifyZoom) {
    this._mapZoom = zoom;

    if (this.mapInitialized) {
      this.mapboxMap.setZoom(zoom.zoom);
      this.setScaledIconSize();
      this.invalidateMapSize();
    }

    this.mapZoomChange.emit(zoom);
  }

  get mapZoom() {
    return this._mapZoom;
  }

  @Input()
  set mapIcons(mapIcons: Icon[]) {
    // cleanup the current markers
    this.markers.forEach(marker => marker.remove());

    // create new markers out of the provided icons
    this.markers = mapIcons.map(icon => {
      const {
        size: { width, height },
        url
      } = icon;

      const markerIcon = L.icon({
        iconUrl: url,
        iconSize: [this.getScaledSize(width), this.getScaledSize(height)],
        // make the icon center match the map center
        iconAnchor: [width / 2, height / 2]
      });

      // remember for recalculation of the size according to the zoom level.
      markerIcon.originalSize = { width, height };

      // try to reuse the icon with the previous coordinate if it not defined
      let iconCenter: Coordinate;
      // because now we support only one icon just take the first one and replace it.
      // in the future if we support multiple icons we should do something like
      // const oldIcon = this.mapIcons.find(i => i.type === icon.type);
      const [oldIcon] = this.mapIcons;

      if (oldIcon && oldIcon.coordinate !== undefined) {
        iconCenter = oldIcon.coordinate;
      } else if (icon.coordinate !== undefined) {
        iconCenter = icon.coordinate;
      } else {
        iconCenter = this.map.center;
      }

      const marker = L.marker(iconCenter, {
        icon: markerIcon,
        draggable: true
      });

      marker.on('moveend', evt => {
        this.updateIcon(icon, marker.getLatLng());
        this.mapIconsChange.emit([icon]);
      });

      // Updates icon position and it's relative size to the mapbox map. It also adds a tooltip
      const syncIconWithMapbox = () => {
        this.updateIcon(icon, marker.getLatLng());

        marker.addTo(this.mapboxMap).bindPopup('Drag the icon to the desired position');
      };

      // we can sync the icon only if the map is initialized (there is a mapboxMap created), otherwise try later!
      // TODO: this is horrible and should be refactored ASAP!
      if (this.mapInitialized) {
        syncIconWithMapbox();
      } else {
        setTimeout(() => {
          syncIconWithMapbox();
        }, 600);
      }

      return marker;
    });

    this._mapIcons = mapIcons;
    this.mapIconsChange.emit(mapIcons);
  }

  get mapIcons(): Icon[] {
    return this._mapIcons;
  }

  @Input()
  set mapCenter(center: Coordinate) {
    const centerChanged = (c1, c2) => c1.lat !== c2.lat || c1.lng !== c2.lng;

    const hasCenterChanged = this.mapCenter !== undefined && centerChanged(center, this.mapCenter);

    this._mapCenter = center;

    if (this.mapInitialized && hasCenterChanged) {
      // only refresh map if the center has really changed, otherwise we endup on an infinite loop because
      // mapbox moveend event is trigger event when the center has not changed!
      this.mapboxMap.setView([center.lat, center.lng], this.mapZoom.zoom);
      this.mapCenterChange.emit(center);
    }
  }

  get mapCenter(): Coordinate {
    return this._mapCenter;
  }

  @Input()
  set mapBounds(bounds: Bounds) {
    this._mapBounds = bounds;
    this.mapBoundsChange.emit(bounds);
  }

  get mapBounds() {
    return this._mapBounds;
  }

  @Input()
  set mapStyle(style: Style) {
    this._mapStyle = style;

    if (this.mapInitialized) {
      this.mapboxMap.eachLayer(layer => {
        this.mapboxMap.removeLayer(layer);
      });

      const newLayer = L.mapbox.tileLayer(style.id);
      this.mapboxMap.addLayer(newLayer);
    }

    this.setStyleSizeFinishDependingProperties();
    // The map size changes when switching between styles due the have different borders. This means that
    // we have to invalidate the map size. Then mapboxMap will move the map and the center will be changed.
    this.invalidateMapSize();
  }

  get mapStyle(): Style {
    return this._mapStyle;
  }

  @Input()
  set mapFinish(finish: Finish) {
    this._mapFinish = finish;

    this.setStyleSizeFinishDependingProperties();
  }

  get mapFinish(): Finish {
    return this._mapFinish;
  }

  constructor(myElement: ElementRef, public sanitizer: DomSanitizer) {
    this.heightRefElement = myElement.nativeElement.parentNode;
    L.mapbox.accessToken = environment.mapbox.accessToken;
  }

  ngAfterViewInit(): void {
    this.initLeaflet();
  }

  private initLeaflet() {
    // wait until the container in which the map is going to be rendered is ready
    // TODO: all these timeouts and hidden dependencies really suck!. We need to either write a manual
    // explaining why it is done this way o find a better more robust why to implement this.
    setTimeout(() => {
      // Why do we need this timeout?
      // 1. Borders occupy map's space so if we render the borders after the mapbox map it will move and change the center.
      // 2. In order to render the borders we need mapStyle, mapSize, mapFinish.
      // so this timeout basically says, let the angular app render the map container and borders and once it is done
      // (after 300ms) mapbox will render the real map in the remaining space.

      this.mapboxMap = L.mapbox.map('map', this.mapStyle.id, this.getMapboxOptions());

      this.mapboxMap.on('moveend', this.updateMap.bind(this));
      // updateMap call is needed to set the bounds
      this.updateMap();
      this.mapInitialized = true;
    }, 300);
  }

  /**
   * Updates icon's coordinate and its sizeAsPercentageOfMap which depends on the current map bounds and zoom.
   */
  private updateIcon(icon: Icon, coordinate: Coordinate): Icon {
    const zoom = this.mapboxMap.getZoom();

    const bottomLeft = this.mapboxMap.project(this.mapboxMap.getBounds()._southWest, zoom);
    const topRight = this.mapboxMap.project(this.mapboxMap.getBounds()._northEast, zoom);

    const mapWidth = (topRight.x - bottomLeft.x) * this.mapZoom.scaling;
    const mapHeight = (bottomLeft.y - topRight.y) * this.mapZoom.scaling;
    console.log(`MapSize: ${mapWidth}x${mapHeight}`);

    // mutation is evil, do it carefully. TODO: find a better way.
    icon.coordinate = coordinate;
    icon.sizeAsPercentageOfMap = {
      width: icon.size.width / mapWidth,
      height: icon.size.height / mapHeight
    };

    return icon;
  }

  /**
   * Returns a scaled size accoring to the map zoom scaling.
   * e.g. unscaled 20 with a scaling of 1/2 -> 40.
   *
   * @param {number} unscaled
   * @return {number}
   */
  private getScaledSize(unscaled: number) {
    return unscaled / this.mapZoom.scaling;
  }

  /**
   * Sets the icon/marker scale according to the map scale.
   */
  private setScaledIconSize() {
    this.mapboxMap.eachLayer(layer => {
      if (layer.options && layer.options.icon && layer.options.icon.originalSize) {
        const originalSize = layer.options.icon.originalSize;
        layer._icon.style.width = `${this.getScaledSize(originalSize.width)}px`;
        layer._icon.style.height = `${this.getScaledSize(originalSize.height)}px`;
      }
    });
  }

  /**
   * updates map center and bounds
   */
  private updateMap() {
    const bounds: { [key: string]: Coordinate } = this.mapboxMap.getBounds();
    const { _northEast, _southWest } = bounds;
    const newCenter: Coordinate = this.mapboxMap.getCenter();

    this.mapBounds = { northEast: bounds._northEast, southWest: bounds._southWest };
    this.mapCenter = newCenter;
  }

  private getMapboxOptions() {
    return {
      center: [this.mapCenter.lat, this.mapCenter.lng],
      zoom: this.mapZoom.zoom,
      attributionControl: false,
      doubleClickZoom: false,
      zoomControl: false,
      boxZoom: false,
      scrollWheelZoom: false,
      touchZoom: false
    };
  }

  zoomIn() {
    this.mapZoom = getZoomLevel(this.mapZoom, this.zoomLevels, 'in');
  }

  zoomOut() {
    this.mapZoom = getZoomLevel(this.mapZoom, this.zoomLevels, 'out');
  }

  public get mapZIndex(): string {
    const layers = this.borders;

    if (layers.length > 1) {
      const innermostLayer = layers[layers.length - 1];
      return innermostLayer.zIndex.toString();
    }

    return '100';
  }

  public get mapBackgroundColor(): string {
    const layers = this.borders;

    if (layers.length > 1) {
      const innermostLayer = layers[layers.length - 1];
      return innermostLayer.background.toString();
    }

    return '#fff';
  }

  public get mapPositionTop(): string | SafeStyle {
    const layers = this.borders;

    if (layers.length > 1) {
      const innermostLayer = layers[layers.length - 1];
      return this.getBorderLayerPositionTop(innermostLayer);
    }

    return '0';
  }

  public get mapPositionRight(): string | SafeStyle {
    const layers = this.borders;

    if (layers.length > 1) {
      const innermostLayer = layers[layers.length - 1];
      return this.getBorderLayerPositionRight(innermostLayer);
    }

    return '0';
  }

  public get mapPositionBottom(): string | SafeStyle {
    const layers = this.borders;

    if (layers.length > 1) {
      const innermostLayer = layers[layers.length - 1];
      return this.getBorderLayerPositionBottom(innermostLayer);
    }

    return '0';
  }

  public get mapPositionLeft(): string | SafeStyle {
    const layers = this.borders;

    if (layers.length > 1) {
      const innermostLayer = layers[layers.length - 1];
      return this.getBorderLayerPositionLeft(innermostLayer);
    }

    return '0';
  }

  public getBorderLayerWidth(layer: Layer): SafeStyle {
    if (layer.width) {
      return this.muToCss(layer.width);
    } else {
      return 'auto';
    }
  }

  public getBorderLayerPositionTop(layer: Layer): SafeStyle {
    if (layer.position.top) {
      return this.muToCss(layer.position.top);
    } else {
      return this.sanitizer.bypassSecurityTrustStyle('auto');
    }
  }

  public getBorderLayerPositionLeft(layer: Layer): SafeStyle {
    if (layer.position.left) {
      return this.muToCss(layer.position.left);
    } else {
      return this.sanitizer.bypassSecurityTrustStyle('auto');
    }
  }

  public getBorderLayerPositionBottom(layer: Layer): SafeStyle {
    if (layer.position.bottom) {
      return this.muToCss(layer.position.bottom);
    } else {
      return this.sanitizer.bypassSecurityTrustStyle('auto');
    }
  }

  public getBorderLayerPositionRight(layer: Layer): SafeStyle {
    if (layer.position.right) {
      return this.muToCss(layer.position.right);
    } else {
      return this.sanitizer.bypassSecurityTrustStyle('auto');
    }
  }

  /**
   * Converts mapify units (mu) to css units.
   *
   * @param {LayerPosition} position
   * @return {SafeStyle}
   */
  public muToCss(position: LayerPosition | LineDim): SafeStyle {
    return this.sanitizer.bypassSecurityTrustStyle(this.muToCssString(position));
  }

  /**
   * Converts mapify units (mu) to a css string.
   *
   * @param {LayerPosition | LineDim} position
   * @return {string}
   */
  private muToCssString(position: LayerPosition | LineDim): string {
    let cssPos = '0';

    function getCssPos(el: HTMLElement, pos: LayerPosition | LineDim): string {
      // This is ugly but required.
      // Basically flexbox positioning does not work on Safari when elements are positioned with `position: absolute`.
      // Due to this we have to center elements on the map usings `transform: translateX(-50%)`
      // https://stackoverflow.com/questions/43779733/absolutely-positioning-with-flexbox-in-safari
      const transform = window.getComputedStyle(el).webkitTransform || undefined;
      // TODO: consider IE
      const cssMatrix = new WebKitCSSMatrix(transform);
      // the default offsetLeft value does not consider the possible transformations that the element could have applied.
      // We have to add the 3D matrix value in the fourth row and first column.
      const offsetLeft = el.offsetLeft + cssMatrix.m41;
      const left = offsetLeft + el.offsetWidth;

      return `calc(${left}px - ${pos.value}${pos.unit})`;
    }

    if (position && position.ref) {
      if (position.ref.leftOf) {
        const refElement = <HTMLElement>document.querySelector('#' + position.ref.leftOf);

        if (refElement) {
          cssPos = getCssPos(refElement, position);
        }
      } else if (position.ref.rightOf) {
        const refElement = <HTMLElement>document.querySelector('#' + position.ref.rightOf);

        if (refElement) {
          cssPos = getCssPos(refElement, position);
        }
      } else if (position.ref.widthOf) {
        const refSelectors = position.ref.widthOf.map(refId => {
          return '#' + refId;
        });

        const refElements = document.querySelectorAll(refSelectors.join(','));

        if (refElements) {
          const refElementWidths: number[] = Array.from(refElements).map((el: HTMLElement) => el.offsetWidth);

          const refWidth = Math.max(...refElementWidths);
          cssPos = refWidth * position.value + 'px';
        }
      }
    } else if (position) {
      switch (position.unit) {
        case 'mh':
          cssPos = this.heightRefElement.offsetHeight * position.value + 'px';
          break;

        case '%':
          cssPos = position.value * 100 + '%';
          break;

        case 'px':
          cssPos = position.value + 'px';
          break;

        case 'em':
          cssPos = position.value + 'em';
          break;

        case 'auto':
          cssPos = 'auto';
          break;
      }
    }

    return cssPos;
  }

  public getTextShadowStyle(text: MapifyMapText): string {
    let shadowStyle;

    if (text.border) {
      shadowStyle =
        '${value}${unit} ${value}${unit} 0 ${color},-${value}${unit} -${value}${unit} 0 ${color},' +
        '${value}${unit} -${value}${unit} 0 ${color},-${value}${unit} ${value}${unit} 0 ${color},' +
        '${value}${unit} 0 0 ${color},-${value}${unit} 0 0 ${color},0 ${value}${unit} 0 ${color},' +
        '0 -${value}${unit} 0 ${color}';

      shadowStyle = shadowStyle.replace(/\$\{value\}/g, text.border.value);
      shadowStyle = shadowStyle.replace(/\$\{unit\}/g, text.border.unit);
      shadowStyle = shadowStyle.replace(/\$\{color\}/g, text.border.color);
    } else {
      shadowStyle = 'none';
    }

    return shadowStyle;
  }

  private invalidateMapSize() {
    if (this.mapboxMap) {
      // wait until the container in which the map is going to be rendered is ready
      window.setTimeout(() => {
        // don't animate!
        this.mapboxMap.invalidateSize(false);
      }, 0);
    }
  }

  getBoxShadow(border: LineBorder): SafeStyle {
    const boxShadow = `0 0 0 ${this.muToCssString(border.width)} ${border.color}`;

    return this.sanitizer.bypassSecurityTrustStyle(boxShadow);
  }

  private setStyleSizeFinishDependingProperties() {
    if (this.mapStyle && this.mapSize && this.mapFinish) {
      const styleName = this.mapStyle.name;
      const orientation = this.mapSize.orientation;
      const finish = this.mapFinish.id;

      if (
        environment.mapify.mapStyle[styleName] &&
        environment.mapify.mapStyle[styleName][orientation] &&
        environment.mapify.mapStyle[styleName][orientation][finish]
      ) {
        if (environment.mapify.mapStyle[styleName][orientation][finish].texts) {
          this.map.texts = environment.mapify.mapStyle[styleName][orientation][finish].texts;
        } else {
          this.map.texts = {};
        }

        if (environment.mapify.mapStyle[styleName][orientation][finish].lines) {
          this.lines = <Line[]>environment.mapify.mapStyle[styleName][orientation][finish].lines;
        } else {
          this.lines = [];
        }

        if (environment.mapify.mapStyle[styleName][orientation][finish].borders) {
          this.borders = <Layer[]>environment.mapify.mapStyle[styleName][orientation][finish].borders;
        } else {
          this.borders = [];
        }
      } else {
        this.borders = [];
        this.lines = [];
        this.map.texts = {};
      }
    } else {
      this.borders = [];
      this.map.texts = {};
    }
  }
}



// WEBPACK FOOTER //
// /vercel/workpath0/src/app/components/map/map.component.ts